/**
 * Shared labels/helpers for the NiV messages tab (policy / inbox / outbox /
 * templates / usage). Registry comes straight from the authored
 * src/config/messageTypes.js so the UI never hardcodes type ids.
 */
import { MESSAGE_TYPES, MESSAGE_TYPE_IDS } from "../../../../config/messageTypes";

export { MESSAGE_TYPES, MESSAGE_TYPE_IDS };

export const CHANNEL_LABELS = { whatsapp: "וואטסאפ", sms: "SMS" };
export const GROUP_LABELS = { patients: "מטופלים", users: "לקוחות / ניב" };

/** The three orders NiV may pick per type (plus "default" = registry). */
export const ORDER_OPTIONS = [
  { value: "default", label: "ברירת מחדל" },
  { value: "whatsapp,sms", label: "וואטסאפ ← SMS" },
  { value: "sms", label: "SMS בלבד" },
  { value: "sms,whatsapp", label: "SMS ← וואטסאפ" },
];

export const OUTCOME_LABELS = {
  sent_whatsapp: "נשלח בוואטסאפ",
  sent_sms_fallback: "SMS (גיבוי)",
  sent_sms_direct: "SMS",
  failed_both: "נכשל בשני הערוצים",
  failed_provider: "נכשל",
  skipped_quota: "דולג: מכסה",
  skipped_no_phone: "דולג: אין טלפון",
  skipped_opt_out: "דולג: הסרה",
  skipped_disabled_by_niv: "דולג: כבוי",
  skipped_unknown_type: "דולג: סוג לא מוכר",
  sent: "נשלח",
  delivered: "נמסר",
  read: "נקרא",
  failed: "נכשל",
};

export const REASON_LABELS = {
  template_missing: "אין תבנית וואטסאפ",
  template_disabled: "תבנית כבויה",
  template_not_approved: "תבנית לא מאושרת",
  template_paused: "תבנית מושהית",
  channel_unconfigured: "ערוץ לא מוגדר",
  recipient_not_whatsapp: "למספר אין וואטסאפ",
  no_sms_body: "אין טקסט SMS",
  sms_not_configured: "SMS לא מוגדר",
};

export const META_STATUS_LABELS = {
  APPROVED: { label: "מאושר", tone: "ok" },
  PENDING: { label: "בבדיקה", tone: "warn" },
  IN_APPEAL: { label: "בערעור", tone: "warn" },
  REJECTED: { label: "נדחה", tone: "bad" },
  PAUSED: { label: "מושהה", tone: "bad" },
  DISABLED: { label: "מבוטל", tone: "bad" },
  MISSING: { label: "לא קיים ב-Meta", tone: "muted" },
};

export const IDENTITY_LABELS = {
  patient: "מטופל/ת",
  lead: "ליד",
  prospect: "מועמד/ת",
  owner: "בעל/ת קליניקה",
  unknown: "לא מזוהה",
};

export const INBOX_KIND_LABELS = {
  text: "טקסט",
  button: "כפתור",
  button_unknown: "כפתור לא מוכר",
  opt_out: "הסרה",
  media: "מדיה",
  reaction: "תגובה",
  other: "אחר",
};

export function describeReason(reason) {
  if (!reason) return "";
  if (reason.startsWith("wa_send_failed:")) return `וואטסאפ נכשל (Meta ${reason.split(":")[1]})`;
  return REASON_LABELS[reason] || reason;
}

export function formatWhen(iso, withDate = true) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("he-IL", {
      timeZone: "Asia/Jerusalem",
      ...(withDate ? { day: "2-digit", month: "2-digit" } : {}),
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

export function maskPhone(phone) {
  const s = String(phone || "");
  if (s.length < 7) return s || "—";
  return `${s.slice(0, 6)}***${s.slice(-3)}`;
}

/** Effective order for a type: NiV override → registry → kill switches (mirrors messagingPolicy.resolveChannelOrder). */
export function effectiveOrder(policy, typeId) {
  const reg = MESSAGE_TYPES[typeId];
  const override = policy?.types?.[typeId] || {};
  const enabledByDefault = reg?.defaultEnabled !== false;
  const enabled = override.enabled === undefined ? enabledByDefault : override.enabled !== false;
  if (!enabled) return { enabled: false, channels: [], source: "niv_disabled" };
  let channels = override.channels?.length ? [...override.channels] : [...(reg?.channels || ["whatsapp", "sms"])];
  const source = override.channels?.length ? "override" : "registry";
  if (policy?.whatsappEnabled === false) channels = channels.filter((c) => c !== "whatsapp");
  if (policy?.smsEnabled === false) channels = channels.filter((c) => c !== "sms");
  if (policy?.fallbackEnabled === false) channels = channels.slice(0, 1);
  return { enabled: channels.length > 0, channels, source };
}

/** Converts a stamp from an outbox row for MessagingStatusBadge. */
export function stampFromRow(row) {
  if (!row) return null;
  if (row.source === "whatsapp") return { channel: "whatsapp", outcome: row.status === "failed" ? "failed_provider" : "sent_whatsapp", status: row.outcome, error: row.error };
  return { channel: row.channel, outcome: row.outcome, fallbackUsed: row.fallbackUsed, fallbackReason: row.fallbackReason, error: row.error };
}
